/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useLazyQuery, useMutation } from '@apollo/client' // Npm: Apollo client.
import {
  Flex,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import MccMncSelector from 'components/MccMncSelector'
import VendorAccountSelector from 'components/VendorAccountSelector'
import PhoneNumberoDirectorySelector from 'components/PhoneNumberoDirectorySelector'


/*
 * GRAPHS
 */
import PhoneNumberoRoutePlanReadQuery from './__query__/index.phonenumberoroutePlan.read.query'
import PhoneNumberoRoutePlanUpdateMutation from './__mutation__/index.phonenumberoroutePlan.update.mutation'
import PhoneNumberoRoutePlanCreateMutation from './__mutation__/index.phonenumberoroutePlan.create.mutation'
import VendorAccountAttachPhoneNumberoRoutePlanMutation from './__mutation__/index.vendorAccount.attachphonenumberoRoutePlan.mutation'


/*
 * OBJECTS
 */
const Index = ({
  isOpen,
  isCreateOnly,
  onClose,
  refetch,
  passOn
}) => {
  // Hook assignment.
  const [vendorAccount, setVendorAccount] = React.useState(void 0)
  const [MutationVendorAccountAttachPhoneNumberoRoutePlan, MutationVendorAccountAttachPhoneNumberoRoutePlanResponse] = useMutation(VendorAccountAttachPhoneNumberoRoutePlanMutation)
  const [error, setError] = React.useState('')
  const [formVersion, _ResetFormInputs] = React.useReducer(j => j + 1, 0)
  const [, _TriggerRender] = React.useReducer(j => j + 1, 0)
  const [QueryPhoneNumberoRoutePlanRead, QueryPhoneNumberoRoutePlanReadResponse] = useLazyQuery(PhoneNumberoRoutePlanReadQuery)
  const [MutationPhoneNumberoRoutePlanCreate, MutationPhoneNumberoRoutePlanCreateResponse] = useMutation(PhoneNumberoRoutePlanCreateMutation)
  const [MutationPhoneNumberoRoutePlanUpdate, MutationPhoneNumberoRoutePlanUpdateResponse] = useMutation(PhoneNumberoRoutePlanUpdateMutation)
  const _formDataRef = React.useRef({})
  const _isCurrentViewMobile = useBreakpointValue({ 'base': false, 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Object assignment.
  const _AttachVendorAccountToPhoneNumberoRoutePlan = async __id => {
    // Only Execute if route has data.
    if (!_.isEmpty(vendorAccount) && vendorAccount.includes('(') && vendorAccount.includes(')')) {
      // Const assignment.
      const vendorAccountId = vendorAccount.split(' ')[1].replace('(', '').replace(')', '')

      // Execute mutation.
      const _MutationVendorAccountAttachPhoneNumberoRoutePlan = await MutationVendorAccountAttachPhoneNumberoRoutePlan({
        'variables': {
          'routePlanId': __id,
          'vendorAccountId': vendorAccountId
        }
      })

      // If mutation caught an exception then report failure.
      if (_MutationVendorAccountAttachPhoneNumberoRoutePlan instanceof Error) return _MutationVendorAccountAttachPhoneNumberoRoutePlan

      // Style Guide.
      toast(_.every(_.pluck(_MutationVendorAccountAttachPhoneNumberoRoutePlan?.data?.VendorAccountAttachPhoneNumberoRoutePlan, 'status'), i => 'UPDATE_SUCCESSFUL' === i) ? 'Successfully attached the vendor with PhoneNumbero route plan' : 'Something went wrong. Please try again after some time.')

      // On successful bind close modal.
      if (_.every(_.pluck(_MutationVendorAccountAttachPhoneNumberoRoutePlan?.data?.VendorAccountAttachPhoneNumberoRoutePlan, 'status'), i => 'UPDATE_SUCCESSFUL' === i)) onClose()
    }

    // Report void 0.
    return void 0
  }
  const _SubmitForm = async e => {
    // Local variable.
    let _routePlanIds

    // Prevent default behavior.
    e.preventDefault()

    // Reset error.
    setError('')

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      vendorAccount: JoiBrowser.string(),
      routeToVendorAccount: JoiBrowser.string(),
      mcc: JoiBrowser.string().required(),
      mnc: JoiBrowser.array().items(JoiBrowser.string().required()).required(),
      phoneNumberoDirectory: JoiBrowser.string(),
      phoneNumbero: JoiBrowser.array().items(JoiBrowser.string()),
      phoneNumberoVendorAccountId: JoiBrowser.string(),
    }).options({ allowUnknown: true });

    // Make sure that mnc is array.
    if (!isCreateOnly && !_.isArray(_formDataRef.current?.mnc)) _formDataRef.current = { ..._formDataRef.current, 'mnc': [(_formDataRef.current?.mnc)?.split('(')[1]?.split(')')[0]] }

    /*
     * Report failure if leastCostRoute is off and vendorAccount
     * priority is not set.
     */
    if (_.isEmpty(_formDataRef.current?.vendorAccount)) return setError('vendorAccount is required.')

    // Remove all keys from _formDataRef.current which are undefined.
    _formDataRef.current = { ..._.pick(_formDataRef.current, _.identity) }

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)
    if (_formDataRef.current.phoneNumbero && 1 <= _formDataRef.current.phoneNumbero.length && _.isEmpty(_formDataRef.current.phoneNumberoVendorAccountId)) return setError('phoneNumberoVendorAccountId is required if you are adding PhoneNumbero based routing.')

    /*
     * Update _formDataRef with routePlanId
     * if it doesn't exist and also make sure that mode
     * is not create only.
     */
    if (!isCreateOnly && _.isEmpty(_formDataRef.current?.routePlanId)) _formDataRef.current = { ..._formDataRef.current, 'routePlanId': passOn?.routePlanId }
    if (isCreateOnly && _.isEmpty(_formDataRef.current?.routeId)) _formDataRef.current = { ..._formDataRef.current, 'routeId': passOn?.routeId }

    /*
     * Update _formDataRef with input and phonenumberoRoutePlan
     * if it doesn't exist. Also make sure that mode is
     * not create only.
     */
    if (_.isEmpty(_formDataRef.current?.input)) _formDataRef.current = ({ 'input': [_formDataRef.current], ..._formDataRef.current })

    // Execute update mutation.
    const _MutationPhoneNumberoRoutePlanUpdate = await [isCreateOnly ? MutationPhoneNumberoRoutePlanCreate : MutationPhoneNumberoRoutePlanUpdate]?.[0]({
      'variables': {
        'routePlanId': _.isEmpty(passOn?.routePlanIds) ? isCreateOnly ? '' : passOn?.routePlanId : void 0,
        'routePlanIds': _.isEmpty(passOn?.routePlanIds) ? void 0 : isCreateOnly ? '' : passOn?.routePlanIds,
        'input': _.pick(_formDataRef.current, 'input').input.map(j => {
          // Const assignment.
          let _mcc, _mnc, _phoneNumbero, _phoneNumberoDirectory, _phoneNumberoVendorAccountId

          // Variable assignment.
          _phoneNumberoDirectory = j?.phoneNumberoDirectory
          _phoneNumbero = j?.phoneNumbero
          _phoneNumberoVendorAccountId = j?.phoneNumberoVendorAccountId
          _mcc = j?.mcc
          _mnc = j?.mnc

          // Conditionally update phonenumberoId and phonenumberoIdDirectoryId.
          if (j?.phoneNumberoDirectory && (j?.phoneNumberoDirectory?.includes('(') || j?.phoneNumberoDirectory?.includes(')'))) _phoneNumberoDirectory = j?.phoneNumberoDirectory.split('(')[1].split(')')[0]
          if (j?.phoneNumbero && 0 < j?.phoneNumbero?.length) _phoneNumbero = _.compact(_.flatten(j?.phoneNumbero.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))
          if (j?.phoneNumberoVendorAccountId && (j?.phoneNumberoVendorAccountId?.includes('(') || j?.phoneNumberoVendorAccountId?.includes(')'))) _phoneNumberoVendorAccountId = j?.phoneNumberoVendorAccountId.split('(')[1].split(')')[0]
          if (j?.mcc && (j?.mcc?.includes('(') || j?.mcc?.includes(')'))) _mcc = j.mcc.split('(')[1].split(')')[0]
          if (j?.mnc && 0 < j?.mnc?.length) _mnc = _.compact(_.flatten(j?.mnc.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))

          // Return data.
          return ({
            'mcc': _mcc,
            'mnc': _mnc,
            'phoneNumberoDirectoryId': _phoneNumberoDirectory,
            'phoneNumbero': _phoneNumbero,
            'phoneNumberoVendorAccountId': _.isEmpty(_phoneNumbero) ? void 0 : _phoneNumberoVendorAccountId,
            'vendorAccountt': void 0,
            'vendorAccounttDirectory': void 0,
            ...(isCreateOnly ? { 'routeId': passOn?.routeId } : {})
          })
        })
      }
    })

    // If mutation caught an exception then report failure.
    if (_MutationPhoneNumberoRoutePlanUpdate instanceof Error) return _MutationPhoneNumberoRoutePlanUpdate

    // If Update contains data then update routePlanIds.
    if (!_.isEmpty(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanUpdate)) _routePlanIds = _.compact(_.pluck(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanUpdate, 'id'))
    if (!_.isEmpty(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanCreate)) _routePlanIds = _.compact(_.pluck(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanCreate, 'id'))

    // Attach vendor account and route to customer.
    !_.isEmpty(_routePlanIds) && _.isArray(_routePlanIds) && await _AttachVendorAccountToPhoneNumberoRoutePlan(_routePlanIds)

    // Check if create was successful.
    if (!_.isEmpty(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanCreate) && _.every(_.pluck(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanCreate, 'status') ?? [], j => 'CREATE_SUCCESSFUL' === j)) {
      // Style guide.
      toast('Successfully upserted the PhoneNumbero route plan.')

      // On successful create close modal.
      refetch?.()
      return onClose?.()
    }
    if (!_.isEmpty(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanUpdate) && _.every(_.pluck(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanUpdate, 'status') ?? [], j => 'UPDATE_SUCCESSFUL' === j)) {
      // Style guide.
      toast('Successfully upserted the PhoneNumbero route plan.')

      // Report failure.
      refetch?.()
      return onClose?.()
    }

    // Style Guide.
    toast((_.isEmpty(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanCreate) ? [0 === _.pluck(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanUpdate, 'message')?.length ? 'Same plan already exists. kindly delete it or modify it instead.' : _.pluck(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanUpdate, 'message')]?.join(' ') : _.pluck(_MutationPhoneNumberoRoutePlanUpdate?.data?.PhoneNumberoRoutePlanCreate, 'message')?.join(' ')))

    // Report void.
    return void 0
  }

  // Event handler.
  React.useEffect(() => {
    // Only prepare the form while the modal is open.
    if (!isOpen) return

    // Create mode should reset local form state without hitting read query.
    if (isCreateOnly) {
      _formDataRef.current = {}
      setVendorAccount(void 0)
      setError('')
      _ResetFormInputs()
      return
    }

    if (_.isEmpty(passOn?.routePlanId)) return

    let _isActive = true

    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QueryPhoneNumberoRoutePlanReadQuery = await QueryPhoneNumberoRoutePlanRead({ 'variables': { 'routePlanId': passOn?.routePlanId } })

      if (!_isActive) return void 0

      // If query caught an exception then report failure.
      if (_QueryPhoneNumberoRoutePlanReadQuery instanceof Error) return _QueryPhoneNumberoRoutePlanReadQuery

      /*
       * If phonenumberoRoutePlan details fetch complete then
       * update its value.
       */

      if (_.first(_QueryPhoneNumberoRoutePlanReadQuery?.data?.PhoneNumberoRoutePlanRead)?.status === "READ_SUCCESSFUL") {
        // Const assignment.
        const _phonenumberoRoutePlanRead = _.first(_QueryPhoneNumberoRoutePlanReadQuery?.data?.PhoneNumberoRoutePlanRead)

        // Update form data.
        _formDataRef.current = {
          'routePlanId': passOn?.routePlanId,
          'createdAt': _phonenumberoRoutePlanRead?.createdAt,
          'updatedAt': _phonenumberoRoutePlanRead?.updatedAt,
          'mcc': _phonenumberoRoutePlanRead?.Mcc?.mcc ? `${_phonenumberoRoutePlanRead?.Mcc?.countryName} (${_phonenumberoRoutePlanRead?.Mcc?.mcc})` : void 0,
          'mnc': _phonenumberoRoutePlanRead?.Mnc?.mnc ? `(${_phonenumberoRoutePlanRead?.Mnc?.mnc}) ${_phonenumberoRoutePlanRead?.Mnc?.network}` : void 0,
          'phoneNumbero': _.isEmpty(_phonenumberoRoutePlanRead?.PhoneNumbero) ? void 0 : _phonenumberoRoutePlanRead?.PhoneNumbero?.map?.(j => `${j?.phoneNumbero} (${j?.id})`),
          'phoneNumberoDirectory': _phonenumberoRoutePlanRead?.PhoneNumberoDirectory?.displayName ? `${_phonenumberoRoutePlanRead?.PhoneNumberoDirectory?.displayName} (${_phonenumberoRoutePlanRead?.PhoneNumberoDirectory?.id})` : void 0,
          'countryName': _phonenumberoRoutePlanRead?.Mcc?.countryName,
          'phoneNumberoVendorAccountId': _phonenumberoRoutePlanRead?.PhoneNumberoVendorAccount?.displayName ? `${_phonenumberoRoutePlanRead?.PhoneNumberoVendorAccount?.displayName} (${_phonenumberoRoutePlanRead?.PhoneNumberoVendorAccount?.id})` : _phonenumberoRoutePlanRead?.VendorAccount?.displayName ? `${_phonenumberoRoutePlanRead?.VendorAccount?.displayName} (${_phonenumberoRoutePlanRead?.VendorAccount?.id})` : void 0,
          'vendorAccount': vendorAccount ?? 0 < _QueryPhoneNumberoRoutePlanReadQuery?.data?.PhoneNumberoRoutePlanRead?.length ? _phonenumberoRoutePlanRead?.VendorAccount?.displayName ? `${_phonenumberoRoutePlanRead?.VendorAccount?.displayName} (${_phonenumberoRoutePlanRead?.VendorAccount?.id})` : void 0 : void 0,
          'vendorAccounttDirectory': _phonenumberoRoutePlanRead?.VendorAccounttDirectory?.displayName ? `${_phonenumberoRoutePlanRead?.VendorAccounttDirectory?.displayName} (${_phonenumberoRoutePlanRead?.VendorAccounttDirectory?.id})` : void 0,
          'vendorAccountt': _.isEmpty(_phonenumberoRoutePlanRead?.VendorAccountt) ? void 0 : _phonenumberoRoutePlanRead?.VendorAccountt?.map?.(j => j?.displayName ? `${j?.displayName} (${j?.id})` : void 0)
        }

        // Reset uncontrolled inputs once loaded data is ready.
        return _ResetFormInputs()
      }

      // Report failure.
      return void 0
    }; _Async()
    return () => {
      _isActive = false
    }
  }, [QueryPhoneNumberoRoutePlanRead, _ResetFormInputs, isCreateOnly, isOpen, passOn?.routePlanId])

  // Const assignment.
  const _isLoading = MutationVendorAccountAttachPhoneNumberoRoutePlanResponse.loading || MutationPhoneNumberoRoutePlanUpdateResponse.loading || MutationPhoneNumberoRoutePlanCreateResponse.loading
  const _isInputDisabled = isCreateOnly ? false : _isLoading || QueryPhoneNumberoRoutePlanReadResponse.loading

  // Return component.
  return (
    <form onSubmit={_SubmitForm} className='phonenumberoRoutePlanUpsert' key={formVersion}>
      <Flex gap='22px' flexDir='column' wrap='nowrap' w='100%'>
        <Flex width='100%' gap='18px' direction={{ 'base': 'column', 'md': 'row' }}>
          <VendorAccountSelector
            disabled={_isInputDisabled}
            name='vendorAccount'
            label='Vendor Account'
            placeholder='e.g. "Awesome Vendor Account"'
            value={_formDataRef?.current?.vendorAccount}
            isRequired={true}
            error={error}
            height='40px'
            isInvalid={error?.includes('vendorAccount') && !error?.includes('position')}
            onChange={({ target }) => {
              // Over spreading.
              const { name, value } = target

              // Update form data.
              _formDataRef.current = {
                ..._formDataRef?.current,
                [name]: value,
                'phoneNumberoVendorAccountId': value
              }

              // Update vendorAccount.
              setVendorAccount(value)
            }}
          />



        </Flex>
        <Flex width='100%' gap='22px' direction={{ 'base': 'column', 'md': 'row' }}>
          <MccMncSelector
            isRequired={true}
            disabled={_isInputDisabled}
            mccValue={_formDataRef?.current?.mcc}
            mncValue={_formDataRef?.current?.mnc}
            inValidMnc={error?.includes('mnc')}
            inValidMcc={error?.includes('mcc')}
            isMccAndMncGlobal={false}
            onChange={i => {
              // Update form data.
              _formDataRef.current = ({ ..._formDataRef.current, 'mnc': i.mnc, 'mcc': i.mcc })

              // Force re-render.
              _TriggerRender()
            }}
          />
          <PhoneNumberoDirectorySelector
            disabled={_isInputDisabled}
            phoneNumberoDirectoryValue={_formDataRef?.current?.phoneNumberoDirectory ?? ''}
            phoneNumberoValue={_formDataRef?.current?.phoneNumbero}
            inValidPhoneNumbero={error?.includes('phoneNumbero')}
            inValidPhoneNumberoDirectory={error?.includes('phoneNumberoDirectory')}
            onChange={i => {
              // Update form data.
              _formDataRef.current = ({ ..._formDataRef.current, 'phoneNumbero': i.phoneNumbero, 'phoneNumberoDirectory': i.phoneNumberoDirectory })

              // Force re-render.
              _TriggerRender()
            }}
          />
        </Flex>

      </Flex>
      <Flex
        mt='22px'
        alignItems={_isCurrentViewMobile ? 'flex-end' : 'center'}
        justifyContent='space-between'
        flexDir='row'>
        <SubmitButton
          mt={0}
          disabled={_isInputDisabled}
          onSubmit={_SubmitForm}
          defaultText={isCreateOnly ? 'Create PhoneNumbero Route Plan' : 'Update PhoneNumbero Route Plan'}
          isLoading={_isLoading} />
      </Flex>
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isCreateOnly': PropTypes.bool,
  'isOpen': PropTypes.bool,
  'onClose': PropTypes.func,
  'refetch': PropTypes.func,
  'passOn': PropTypes.object,
  'submitRef': PropTypes.object,
  'disabled': PropTypes.bool,
  'isUpsertMixerContext': PropTypes.bool,
  'setSubmitIsLoading': PropTypes.func
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(React.memo(Index))
